// ============================================================================
// TASKS — the derivations behind LifeOS Tasks.dc.html (option 8a).
//
// EVERY NUMBER ON THE TASKS SCREEN IS COMPUTED HERE, none of it stored. The handoff README puts
// counts and time-remaining strings under "Derived, never stored" (§State Management), and a
// stored count is a number that goes silently wrong the first time a row is ticked.
//
// It is a module rather than a DataAPI method on purpose: `getTasks()` already exposes every
// task through the contract, so an LLM asking the Worker for the same board can compute the same
// board. Adding getTaskBoard() would put the same arithmetic in two places and give it two
// chances to disagree. (components/school/derive.ts is the precedent.)
// ============================================================================

import {
  daysBetween,
  relativeDay,
  toDate,
  weekdayName,
  type ISODate,
  type Task,
} from '@app/shared'

import { clockFromTimestamp, localDayOf } from '../../lib/time'

/** How far ahead the board's "Upcoming · next 7 days" card looks. The mockup's own window. */
export const SOON_DAYS = 7

/** How long a completion is kept before it is archived, per the Done log's own footer. */
export const DONE_LOG_KEPT_DAYS = 90

export type LensId = 'inbox' | 'today' | 'upcoming' | 'someday' | 'done'

export interface TaskListRow {
  /** The list's own name — the string `Task.list` carries. */
  name: string
  /** Open tasks filed under it. */
  open: number
}

export interface TaskBoard {
  today: ISODate
  /** Open and already past its date. Pinned, never softened, never folded into `openCount`. */
  overdue: Task[]
  /** Open and due today. */
  dueToday: Task[]
  /** Open and due inside the next seven days, tomorrow onward. */
  soon: Task[]
  /** Open and dated beyond that window. */
  later: Task[]
  /** Open with no date at all. No date is invented for these. */
  someday: Task[]
  /** Open and unfiled — captured with no list. A subset of `someday`. */
  inbox: Task[]
  /**
   * THE HEADER'S "9 open".
   *
   * Open work inside the board's window: due today, or inside the next seven days. Overdue is
   * counted SEPARATELY and never folded in — folding it in would let a six-day-old task vanish
   * into a bigger number, which is the softening this screen exists to refuse (README §7:
   * "Overdue items are always pinned and never softened"). The band prints both, side by side,
   * and its tooltip says exactly what each one counts.
   *
   * `someday` is not open work: it is a backlog with no claim on any day, and counting it here
   * would make the number meaningless the moment the backlog grew.
   */
  openCount: number
  /** Everything not done, whatever its date. The rail's attention dot reads the same tasks. */
  lists: TaskListRow[]
}

/** The list a task carries when it was captured without one. Not a real list — an absence. */
export const UNFILED = 'Inbox'

function byDue(a: Task, b: Task): number {
  if (a.due === b.due) return a.title < b.title ? -1 : 1
  if (a.due === null) return 1
  if (b.due === null) return -1
  return a.due < b.due ? -1 : 1
}

export function buildBoard(tasks: Task[], today: ISODate): TaskBoard {
  const open = tasks.filter((t) => !t.done)
  const dated = open.filter((t) => t.due !== null)

  const overdue = dated.filter((t) => t.due! < today).sort(byDue)
  const dueToday = dated.filter((t) => t.due === today).sort(byDue)
  const soon = dated
    .filter((t) => t.due! > today && daysBetween(today, t.due!) <= SOON_DAYS)
    .sort(byDue)
  const later = dated.filter((t) => daysBetween(today, t.due!) > SOON_DAYS).sort(byDue)
  const someday = open.filter((t) => t.due === null).sort(byDue)
  const inbox = someday.filter((t) => t.list === UNFILED)

  const counts = new Map<string, number>()
  for (const t of open) {
    if (t.list === UNFILED) continue // unfiled is an absence of a list, not a list
    counts.set(t.list, (counts.get(t.list) ?? 0) + 1)
  }

  return {
    today,
    overdue,
    dueToday,
    soon,
    later,
    someday,
    inbox,
    openCount: dueToday.length + soon.length,
    lists: [...counts.entries()]
      .map(([name, count]) => ({ name, open: count }))
      .sort((a, b) => (a.name < b.name ? -1 : 1)),
  }
}

/**
 * OVERDUE BELONGS TO TODAY, and this function is the sentence saying so.
 *
 * An overdue task is late work you are meant to do NOW — Today is the only lens that can be
 * true about it. `Upcoming` asks for a date after today and `Someday` asks for no date at all,
 * so a task that is merely late falls out of both; before this existed it fell out of ALL of
 * them and lived only in the header's count, which is a number you cannot tick (owner report,
 * 2026-08-28: a task due 2026-08-26 was counted "1 overdue" and could not be seen or completed).
 *
 * It does NOT get folded into `openCount` — see that field's note. Counted in the lens that
 * shows it, counted apart in the band that warns about it; those are two different questions.
 */
// `string`, not `LensId`: the phone's board has its own four-lens vocabulary (`week` where the
// desktop says `upcoming`), and both of them call the first one `today`. One rule, both screens.
export function lensOwnsOverdue(lens: string): boolean {
  return lens === 'today'
}

export function lensCount(board: TaskBoard, lens: LensId): number | null {
  switch (lens) {
    case 'inbox':
      return board.inbox.length
    case 'today':
      return board.overdue.length + board.dueToday.length
    case 'upcoming':
      return board.soon.length + board.later.length
    case 'someday':
      return board.someday.length
    // The Done log carries no count in the lens column — its own header states one, and two
    // numbers for the same thing in one column is how they start disagreeing.
    case 'done':
      return null
  }
}

/**
 * Does this lens genuinely hold nothing? The one question the "nothing here" copy is allowed to
 * answer — and it has to know about the pinned overdue card, or the screen prints "Nothing is due
 * today" directly under a red card listing something that is.
 */
export function isEmptyLens(board: TaskBoard, lens: LensId): boolean {
  if (lensOwnsOverdue(lens) && board.overdue.length > 0) return false
  switch (lens) {
    case 'inbox':
      return board.inbox.length === 0
    case 'today':
      return board.dueToday.length === 0 && board.soon.length === 0
    case 'upcoming':
      return board.soon.length + board.later.length === 0
    case 'someday':
      return board.someday.length === 0
    case 'done':
      return false
  }
}

// ── The done log ────────────────────────────────────────────────────────────

export interface DoneDay {
  date: ISODate
  label: string
  tasks: Task[]
}

/**
 * Reads the calendar day a completion happened on. Injectable ONLY so a test can pin a zone;
 * every caller in the app takes the default, which is the browser's own clock — the same zone
 * the rest of this screen formats in.
 */
export type DayOfTimestamp = (timestamp: string) => ISODate

/**
 * Completions grouped by the day they happened, newest day first.
 *
 * THE DAY IS THE LOCAL ONE, never `completedAt.slice(0, 10)`. A task finished at 11:03 PM on a
 * Wednesday in California carries `2026-08-28T06:03Z`; the slice files it under Thursday and the
 * log then heads it `TOMORROW · FRI 28`. A completion cannot be tomorrow (owner report,
 * 2026-08-28).
 */
export function doneDays(
  tasks: Task[],
  today: ISODate,
  dayOf: DayOfTimestamp = localDayOf,
): DoneDay[] {
  const groups = new Map<ISODate, Task[]>()
  for (const t of tasks) {
    if (!t.done || t.completedAt === null) continue
    const date = dayOf(t.completedAt)
    const bucket = groups.get(date)
    if (bucket) bucket.push(t)
    else groups.set(date, [t])
  }
  return [...groups.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([date, rows]) => ({
      date,
      label: dayGroupLabel(date, today),
      // Newest completion first inside the day, so ticking something puts it at the top of TODAY.
      tasks: rows.sort((a, b) => (a.completedAt! < b.completedAt! ? 1 : -1)),
    }))
}

/**
 * Completions in the last seven days — the Done log header's "11 this week". Same local day as
 * the grouping above, from the same function, so the count and the list cannot disagree about
 * which week something happened in.
 */
export function doneThisWeek(
  tasks: Task[],
  today: ISODate,
  dayOf: DayOfTimestamp = localDayOf,
): number {
  return tasks.filter(
    (t) => t.done && t.completedAt !== null && daysBetween(dayOf(t.completedAt), today) <= SOON_DAYS,
  ).length
}

// ── Row and group strings ───────────────────────────────────────────────────

/**
 * 'TODAY' · 'TOMORROW · FRI 21' · 'MON 24' — the day headers inside a grouped card and in the
 * Done log. Uppercase is the eyebrow style, not a shout.
 *
 * ONLY TWO DAYS GET A WORD. The mockup names today and tomorrow and nothing else: its done log
 * heads yesterday as plain `WED 19`, because in a log of finished things the weekday is what you
 * are actually scanning for. A `YESTERDAY ·` prefix there would be one more word between the eye
 * and the date.
 */
export function dayGroupLabel(date: ISODate, today: ISODate): string {
  const stamp = `${weekdayName(date, true).toUpperCase()} ${toDate(date).getUTCDate()}`
  const delta = daysBetween(today, date)
  if (delta === 0) return 'TODAY'
  if (delta === 1) return `TOMORROW · ${stamp}`
  return stamp
}

export type DueTone = 'overdue' | 'today' | 'ahead'

export function dueTone(task: Task, today: ISODate): DueTone {
  if (task.due === null) return 'ahead'
  if (task.due < today) return 'overdue'
  if (task.due === today) return 'today'
  return 'ahead'
}

/**
 * 'today' · 'today · 6pm' · 'tomorrow' · 'Mon' · '3 days ago' — the due pill.
 *
 * The clock time is appended ONLY when the reminder is for today. A reminder five days out has a
 * time on it too, and printing it would make the pill read like a scheduled appointment when all
 * the app knows is which day it is about.
 */
export function duePill(task: Task, today: ISODate): string | null {
  if (task.due === null) return null
  const day = relativeDay(task.due, today)
  if (task.reminderAt !== null && task.due === today) {
    return `${day} · ${clockFromTimestamp(task.reminderAt)}`
  }
  return day
}

/**
 * 'weekly' · 'monthly' — the recurrence chip, read off the RRULE.
 *
 * An RRULE this does not recognise returns null and the chip is not drawn. A task that repeats
 * every second Tuesday is not "weekly", and a chip that rounds it to one is a small lie about
 * the user's own schedule.
 */
const FREQ_WORD: Record<string, string> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
}

export function recurrenceWord(rrule: string | null): string | null {
  if (rrule === null) return null
  const parts = rrule.split(';').map((p) => p.trim().toUpperCase())
  // INTERVAL=2 means "every other", which none of these four words says.
  const interval = parts.find((p) => p.startsWith('INTERVAL='))
  if (interval && interval !== 'INTERVAL=1') return null
  const freq = parts.find((p) => p.startsWith('FREQ='))?.slice('FREQ='.length)
  return freq ? (FREQ_WORD[freq] ?? null) : null
}

/** '2 items · oldest 6 days' — the Overdue header's quiet half. */
export function overdueSummary(overdue: Task[], today: ISODate): string {
  const oldest = overdue.reduce(
    (worst, t) => Math.max(worst, daysBetween(t.due!, today)),
    0,
  )
  const items = `${overdue.length} ${overdue.length === 1 ? 'item' : 'items'}`
  return `${items} · oldest ${oldest} ${oldest === 1 ? 'day' : 'days'}`
}
